import { LogLevel, LogService } from "@/services/logging";
import type { IProductCategory, ITax } from "@/types/db/product-category";

/**
 * FIS-712 — taxes of the prices on a tax scheme (Québec GST + QST).
 *
 * The GraphQL catalogue the POS syncs only carries ONE tax per price (`TaxRead` via
 * `VatRateGeneralId`), so a price set on a scheme arrives with no tax at all. The services
 * catalogue (`POST /v1/catalog/product-details`, the same data the online order flow uses)
 * returns the price's full list instead: more than one tax means double VAT, ordered by
 * position (position 0 first).
 *
 * Only the prices that synced WITHOUT a tax are looked up — a scheme price always does, and
 * every other price keeps the GraphQL tax untouched. Any failure (offline, timeout, bad
 * payload) leaves the prices exactly as synced, i.e. today's behaviour.
 */

const BATCH_SIZE = 200;
const TIMEOUT_MS = 15_000;
// order-common VATType.INCLUDED
const VAT_INCLUDED = 1;

interface CatalogTax {
  taxId: number;
  code: string | null;
  name: string | null;
  rate: number; // fraction, e.g. 0.09975
  position: number;
  baseMode: number;
}

interface CatalogProduct {
  priceId: string;
  vatType?: number;
  taxes?: CatalogTax[];
}

/**
 * Catalogue taxes → the POS price taxes, position 0 first
 *
 * @params {CatalogProduct} product: services catalogue product of one price
 * @returns {ITax[]}
 */
function toPriceTaxes(product: CatalogProduct): ITax[] {
  return [...(product.taxes ?? [])]
    .sort((a, b) => a.position - b.position)
    .map((tax) => ({
      id: tax.taxId,
      name: tax.name,
      taxNumber: tax.code ?? undefined,
      rate: undefined,
      rateMiliValue: Math.round(tax.rate * 1_000_000),
      includedInPrice: product.vatType === VAT_INCLUDED ? 1 : 0,
      position: tax.position,
      baseMode: tax.baseMode,
    }));
}

/**
 * Fetch the catalogue products of a batch of prices
 *
 * @params {string[]} priceIds: price uuids
 * @returns {Promise<CatalogProduct[]>}
 */
async function fetchCatalogProducts(
  priceIds: string[]
): Promise<CatalogProduct[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(
      `${import.meta.env.VITE_SERVICES_API}/v1/catalog/product-details`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ priceIds, includeInactive: true }),
        signal: controller.signal,
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const body = await response.json();

    return body?.data?.products ?? body?.products ?? [];
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Give the scheme prices of freshly synced product categories their list of taxes. Mutates
 * the prices in place; never throws.
 *
 * @params {IProductCategory[]} pcs: product categories in local format
 * @returns {Promise<number>} number of prices that received a tax list
 */
export async function attachSchemeTaxes(
  pcs: IProductCategory[]
): Promise<number> {
  if (!import.meta.env.VITE_SERVICES_API) {
    return 0;
  }

  const untaxed = pcs
    .flatMap((pc) => pc.prices ?? [])
    .filter((price) => price?.uuid && !price.taxes?.length);

  if (!untaxed.length) {
    return 0;
  }

  const byUuid = new Map(untaxed.map((price) => [String(price.uuid), price]));
  const uuids = [...byUuid.keys()];
  let schemePrices = 0;

  try {
    for (let i = 0; i < uuids.length; i += BATCH_SIZE) {
      const products = await fetchCatalogProducts(
        uuids.slice(i, i + BATCH_SIZE)
      );

      for (const product of products) {
        const price = byUuid.get(String(product.priceId));

        // a single tax is already handled by the GraphQL sync; only a scheme is double VAT
        if (price && (product.taxes?.length ?? 0) >= 2) {
          price.taxes = toPriceTaxes(product);
          schemePrices += 1;
        }
      }
    }

    LogService.info("Catalog taxes: tax schemes loaded for the synced prices", {
      pricesWithoutTax: uuids.length,
      schemePrices,
    });
  } catch (error) {
    LogService.info(
      "Catalog taxes: could not load the tax scheme of the synced prices, they keep their synced taxes",
      {
        error: error instanceof Error ? error.message : String(error),
        prices: uuids.length,
      },
      LogLevel.Warning
    );
  }

  return schemePrices;
}
